/**
 * src/lib/api/documents.js — project Knowledge-Vault document access (read side).
 *
 * The vault capture (UploadFilesPanel -> documents.register) files dropped PDFs /
 * specs into `project_documents`. This wrapper surfaces them in the project Files
 * tab and mints a short-TTL signed URL so they're viewable in-app.
 *
 * Viewing reuses the bucket's existing `authenticated` SELECT policy
 * (docs_storage_read) — the client signs its own read URL, no server endpoint
 * needed for Supabase-stored docs. Large (>25MB) MinIO-backed docs get a server
 * presign in a later slice; signDocumentUrl returns null for those.
 */

import { apiFetch } from './client';
import { supabase } from '../supabase';

// Supabase buckets we can sign a read URL for client-side (each has an
// authenticated SELECT policy). Docs land in project-documents; image captures
// land in photo-buffer. MinIO/hetzner-backed large files use a different prefix
// and get a server presign in a later slice.
const SIGNABLE_BUCKETS = ['project-documents', 'photo-buffer', 'intake-photos']; // intake-photos: TICKET-1443 (customer intake-form photos, authenticated SELECT policy)

/**
 * The public 320px thumbnail written at capture (`photo-thumbs` bucket, public
 * read) — free to render: no signing, no round-trip, ~30KB instead of the
 * 269-405KB original. Single source for every surface that shows a photo tile;
 * PhotoGallery and ComposeEmailModal previously each carried their own copy.
 * Returns null when the row has no thumb (e.g. quote/brain reference images) so
 * the caller can fall back to a lazy presign of the original.
 */
export function thumbUrlFromDoc(doc) {
  const t = doc?.document_metadata?.storage?.thumb_path;
  // Only an absolute URL renders; a relative path would 404 against the app origin.
  return (typeof t === 'string' && t.startsWith('http')) ? t : null;
}

/**
 * True once the Supabase buffer copy has been removed in favour of the durable
 * MinIO object. `file_path` is NOT updated at eviction, so it is the only
 * reliable signal that signing `file_path` would produce a dead URL.
 */
function isBufferEvicted(doc) {
  const st = doc?.document_metadata?.storage;
  if (!st) return false;
  return !!(st.permanent_key || st.buffer_evicted_at);
}

/**
 * TICKET-772 — the Files tabs list FILED DOCUMENTS; photos belong to the Photos
 * tab. documents.list stamps kind:'document' on every project_documents row, so
 * without this the Files tab shows every site photo (499 of 556 rows estate-wide)
 * and a quotation sits somewhere in the middle of them (CEO 2026-09-05: "files
 * clearly full of images ... I can't find the quote signed").
 *
 * Classification is read from document_type, which the DB already has right — an
 * image filed as something else (a photographed invoice, document_type 'other')
 * is a document and stays.
 */
export function isPhotoDoc(doc) {
  return doc?.document_type === 'photo';
}

/**
 * List the project's filed documents (kind:'document' half of documents.list).
 * Best-effort: the caller decides how to handle a throw (the vault flag may be
 * off server-side -> 404). Returns the document items array.
 */
export async function listProjectDocuments(projectId) {
  if (!projectId) return [];
  const body = await apiFetch(`/api/data?action=documents.list&project_id=${encodeURIComponent(projectId)}`);
  const items = Array.isArray(body?.items) ? body.items : [];
  return items.filter((it) => it && it.kind === 'document' && !isPhotoDoc(it));
}

/**
 * List a DEAL's filed documents — the pipeline Files tab sibling of
 * listProjectDocuments. Email-attachment PDFs are anchored by deal_id, so the deal
 * card surfaces them here. Returns the document items array (best-effort).
 */
export async function listDealDocuments(dealId) {
  if (!dealId) return [];
  const body = await apiFetch(`/api/data?action=documents.list&kind=document&deal_id=${encodeURIComponent(dealId)}`);
  const items = Array.isArray(body?.items) ? body.items : [];
  return items.filter((it) => it && it.kind === 'document' && !isPhotoDoc(it));
}

/**
 * TICKET-392 — the PHOTOS filed against a deal (or its project), for picking into a
 * quote. CEO 2026-07-25: "when we inspect the board, we take a bunch of pictures,
 * and we add them here to the file folder. And when we build the board afterwards,
 * we should be able to pick those images."
 *
 * Same documents.list endpoint as the Files tab, scoped by document_type — no new
 * handler. Best-effort: a failure yields [] so the picker degrades to the template
 * library rather than breaking the builder.
 */
/**
 * TICKET-414 (spec §2.5) — cross-project reference photos for the annex's
 * "hall of fame": comparable work from OTHER boats.
 *
 * The server excludes the current deal/project, so this can never offer back a
 * photo that already belongs to the annex's "project photos" subsection.
 * Synonym-expanded lexical match, NOT vector similarity — see the handler for why.
 * A GET (no `method:`), so it must stay registered under the router's
 * method-agnostic read branch (TICKET-389 class).
 */
/**
 * TICKET-481 slice A — the chip row's data, fetched ONCE when the picker opens.
 *
 * Deliberately a separate call rather than a field on every search response: the
 * search re-runs on each keystroke, and the facet pass is a second bounded query.
 * Paying for it per keystroke to serve a control that never changes would be waste.
 *
 * Returns [{ type, count }] most-photos-first, or [] on any failure — the chip row is
 * an affordance, and losing it must leave the picker working as a plain text browser.
 */
export async function fetchReferencePhotoFacets({ exclude_deal_id, exclude_project_id } = {}) {
  const params = new URLSearchParams({ action: 'documents.search_reference_photos', include_facets: '1', limit: '1' });
  if (exclude_deal_id) params.set('exclude_deal_id', exclude_deal_id);
  if (exclude_project_id) params.set('exclude_project_id', exclude_project_id);
  try {
    const body = await apiFetch(`/api/data?${params.toString()}`);
    return Array.isArray(body?.facets) ? body.facets : [];
  } catch {
    // spine-emit-ok: no chips is a degraded picker, not a broken one; apiFetch emitted
    return [];
  }
}

export async function searchReferencePhotos({ q = '', exclude_deal_id, exclude_project_id, limit, product_types } = {}) {
  const params = new URLSearchParams({ action: 'documents.search_reference_photos' });
  if (q) params.set('q', q);
  if (exclude_deal_id) params.set('exclude_deal_id', exclude_deal_id);
  if (exclude_project_id) params.set('exclude_project_id', exclude_project_id);
  if (limit) params.set('limit', String(limit));
  // Comma-joined: this is a GET, and the handler parses either shape.
  if (Array.isArray(product_types) && product_types.length) {
    params.set('product_types', product_types.join(','));
  }
  try {
    const body = await apiFetch(`/api/data?${params.toString()}`);
    return Array.isArray(body?.items) ? body.items : [];
  } catch {
    // spine-emit-ok: the reference tab degrades to empty; apiFetch already emitted
    // the fetch_trace for the failed call.
    return [];
  }
}

export async function listDealPhotos({ deal_id, project_id } = {}) {
  // TICKET-708 — pass BOTH anchors so the backend can OR them (shared-folder mode).
  // If only one is provided, the backend still works (exact-anchor mode). If neither,
  // return empty (caller's responsibility to provide at least one).
  const anchors = [];
  if (deal_id) anchors.push(`deal_id=${encodeURIComponent(deal_id)}`);
  if (project_id) anchors.push(`project_id=${encodeURIComponent(project_id)}`);
  if (!anchors.length) return [];
  const anchor = anchors.join('&');
  try {
    const body = await apiFetch(`/api/data?action=documents.list&document_type=photo&${anchor}`);
    const items = Array.isArray(body?.items) ? body.items : [];
    return items.filter((it) => it && it.id && it.document_type === 'photo');
  } catch {
    // spine-emit-ok: the picker degrades to the template library when the deal has no
    // readable photos; apiFetch already emits the fetch_trace for the failed call
    return [];
  }
}

/**
 * Resolve an open/download URL for ONE filed document, backend-agnostic:
 *   - Supabase-stored doc in a signable bucket  -> client-side signed URL (no round-trip)
 *   - Hetzner/MinIO-backed doc (hetzner: prefix) -> short-TTL server presign
 *     (documents.download by id; the server branches on the stored scheme)
 * Both pipeline + project Files tabs call this so a PDF opens regardless of where its
 * bytes live. Returns a URL or null (caller shows "preview unavailable" on null).
 */
export async function openDocumentUrl(doc, ttlSeconds = 300) {
  // A photo whose Supabase buffer was evicted to MinIO (Phase-2 offload) exists
  // ONLY at storage.permanent_key — but file_path still names the DELETED buffer
  // object. Signing it succeeds and mints a URL that then 400s on GET (prod
  // 2026-07-27: 41 photos on one deal, evicted 17 minutes after capture, every
  // thumbnail and every open broken). The server presign branches on the stored
  // backend, so hand evicted rows to it instead of signing a dead object.
  if (doc?.file_path && !isBufferEvicted(doc)) {
    const signed = await signDocumentUrl(doc.file_path, ttlSeconds);
    if (signed) return signed;
  }
  if (doc?.id) {
    const body = await apiFetch(`/api/data?action=documents.download&document_id=${encodeURIComponent(doc.id)}`);
    return body?.url || null;
  }
  return null;
}

/**
 * Mint a short-TTL signed read URL for a Supabase-stored file. file_path is
 * `<bucket>/<object>` for a signable bucket (project-documents | photo-buffer);
 * MinIO-backed docs (different prefix) return null (no client-side signing — a
 * later slice adds a server presign). Throws on a real Supabase storage error so
 * the caller surfaces it (never a silent swallow).
 */
export async function signDocumentUrl(filePath, ttlSeconds = 300) {
  if (typeof filePath !== 'string') return null;
  for (const bucket of SIGNABLE_BUCKETS) {
    const prefix = `${bucket}/`;
    if (!filePath.startsWith(prefix)) continue;
    const objectName = filePath.slice(prefix.length);
    if (!objectName) return null;
    const { data, error } = await supabase.storage.from(bucket).createSignedUrl(objectName, ttlSeconds);
    if (error) throw new Error(error.message);
    return data?.signedUrl || null;
  }
  return null;
}

/**
 * Carry a won deal's filed documents + photos into the project it became
 * (TICKET-468). The won-flow already re-homed tasks and bundles; documents were
 * the sibling nobody wrote, which is why `documents.list` needs a
 * `projects.deal_id` read-through just to make a deal's files visible on its own
 * project.
 *
 * Sets project_id and KEEPS deal_id — the deal stays part of the file's history,
 * exactly like the task/bundle carry-over. Only un-homed, non-archived rows are
 * touched, so it is idempotent and safe to re-run.
 *
 * Returns { success, count } — never throws into the won-promotion, which must
 * not be blocked by a re-home miss.
 */
export async function reassignDealDocumentsToProject(dealId, projectId) {
  if (!dealId || !projectId) return { success: false, error: 'missing dealId/projectId' };
  try {
    const { data, error } = await supabase
      .from('project_documents')
      .update({ project_id: projectId })
      .eq('deal_id', dealId)
      .is('project_id', null)
      .is('archived_at', null)
      .select('id');
    if (error) return { success: false, error: error.message };
    return { success: true, count: data?.length || 0 };
  } catch (err) {
    // spine-emit-ok: returns {success:false,error} for the caller (won-promotion)
    // to surface — same structured-error convention as the task/bundle siblings.
    return { success: false, error: err.message };
  }
}

/**
 * Soft-archive a filed document (removes it from the Files tab). Server sets
 * archived_at (restorable — never a hard delete). Management roles only (server
 * returns 403 otherwise). Returns true on success.
 */
export async function archiveDocument(documentId) {
  if (!documentId) return false;
  const body = await apiFetch('/api/data', {
    method: 'POST',
    body: JSON.stringify({ action: 'documents.archive', document_id: documentId }),
  });
  return body?.success === true;
}
